// /hub/login: a Hub (the offline edition on the person's PC) asks to be tied
// to this account. The Hub shows a short code and this address (with the code
// in `?code=` when opened from its link or QR); the person checks which Hub
// it is and connects or refuses it. The Hub takes its own token afterwards.
// A PC using a Hub signs in the same way (`kind` desktop), and is called a PC.
//
// Anyone can start a sign-in and send its link, so a code from the link is
// taken only when the sign-in was started from the same network as this
// browser (`sameSource`); otherwise the person types the code the Hub shows.
// The approval says when and where the sign-in was started.
import { useMutation, useQuery } from "@tanstack/react-query";
import { m } from "@trameria/cloud-i18n";
import { getLocale } from "@trameria/i18n";
import { ApiError, Button, ErrorLine, useApi } from "@trameria/ui-core";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import { SignedOutPage } from "./signed-out-page";

const FIELD =
  "plain-field h-10 w-full rounded-[7px] border border-field bg-surface px-3 text-[15px] tracking-[0.08em] text-fg uppercase outline-none focus:border-field-strong";

interface HubLoginView {
  name: string;
  kind: "hub" | "desktop";
  status: string;
  expiresAt: string;
  requestedAt: string;
  requestIp: string | null;
  requestCountry: string | null;
  sameSource: boolean;
}

/** A code to look up, and whether the person typed it (not from the link). */
interface Asked {
  code: string;
  typed: boolean;
}

export function HubLoginPage({ code: given }: { code?: string }) {
  const [code, setCode] = useState("");
  const [asked, setAsked] = useState<Asked | null>(
    given ? { code: given, typed: false } : null,
  );
  const typed = (c: string) => setAsked({ code: c, typed: true });
  return (
    <SignedOutPage title={m.web_hub_title()}>
      {asked ? (
        <Decide
          asked={asked}
          onAgain={() => setAsked(null)}
          typeCode={
            <CodeForm
              code={code}
              setCode={setCode}
              onNext={typed}
              note={m.web_hub_type_code()}
            />
          }
        />
      ) : (
        <CodeForm code={code} setCode={setCode} onNext={typed} />
      )}
    </SignedOutPage>
  );
}

function CodeForm({
  code,
  setCode,
  onNext,
  note,
}: {
  code: string;
  setCode: (code: string) => void;
  onNext: (code: string) => void;
  note?: string;
}) {
  const id = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.trim()) onNext(code.trim());
  };
  return (
    <form className="flex flex-col gap-4" noValidate onSubmit={submit}>
      {note && <p className="text-[13px] leading-[1.7]">{note}</p>}
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-[12px] text-muted">
          {m.web_hub_code_label()}
        </label>
        <input
          id={id}
          autoComplete="one-time-code"
          // biome-ignore lint/a11y/noAutofocus: the one thing to do on this page
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="XXXX-XXXX"
          className={FIELD}
        />
      </div>
      <Button
        type="submit"
        variant="primary"
        className="h-10 w-full"
        disabled={!code.trim()}
      >
        {m.web_hub_code_next()}
      </Button>
    </form>
  );
}

/** "Japan (203.0.113.5)", or what is known of it. */
function placeOf(view: HubLoginView): string | null {
  let country: string | null = null;
  if (view.requestCountry) {
    try {
      country =
        new Intl.DisplayNames([getLocale()], { type: "region" }).of(
          view.requestCountry,
        ) ?? view.requestCountry;
    } catch {
      country = view.requestCountry;
    }
  }
  if (country && view.requestIp) return `${country} (${view.requestIp})`;
  return country ?? view.requestIp;
}

function Decide({
  asked,
  onAgain,
  typeCode,
}: {
  asked: Asked;
  onAgain: () => void;
  typeCode: ReactNode;
}) {
  const api = useApi();
  const path = `/api/me/hub-logins/${encodeURIComponent(asked.code)}`;
  const view = useQuery({
    queryKey: ["hub-login", asked.code],
    queryFn: () => api<HubLoginView>(path),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const decide = useMutation({
    mutationFn: (accept: boolean) =>
      api<{ status: "approved" | "denied" }>(path, {
        method: "POST",
        body: { accept },
      }),
  });
  if (view.isPending) return <div className="h-[120px]" />;
  if (
    view.isError ||
    (decide.error instanceof ApiError && decide.error.status === 404)
  )
    return (
      <div className="flex flex-col items-start gap-4">
        <p className="text-[13px] leading-[1.7]">{m.web_hub_not_found()}</p>
        <Button onClick={onAgain}>{m.web_hub_code_next()}</Button>
      </div>
    );
  // A code from a link started elsewhere: the person types it themselves.
  if (!asked.typed && !view.data.sameSource) return typeCode;
  const pc = view.data.kind === "desktop";
  if (decide.data)
    return (
      <p className="text-[13px] leading-[1.7]">
        {decide.data.status === "approved"
          ? pc
            ? m.web_hub_desktop_done()
            : m.web_hub_done()
          : pc
            ? m.web_hub_desktop_denied()
            : m.web_hub_denied()}
      </p>
    );
  const name = view.data.name;
  const place = placeOf(view.data);
  const time = new Intl.DateTimeFormat(getLocale(), {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(view.data.requestedAt));
  return (
    <>
      <p className="text-[13px] leading-[1.7]">
        {pc
          ? m.web_hub_desktop_connecting({ name })
          : m.web_hub_connecting({ name })}
      </p>
      <ul className="mt-3 flex flex-col gap-0.5 text-[12px] leading-[1.6] text-muted">
        <li>{m.web_hub_started_at({ time })}</li>
        {place && <li>{m.web_hub_started_from({ place })}</li>}
      </ul>
      <p className="mt-3 text-[13px] leading-[1.7] text-muted">
        {pc ? m.web_hub_desktop_what() : m.web_hub_what()}
      </p>
      <p role="alert" className="mt-4 text-[13px] leading-[1.7] text-danger">
        {m.web_hub_warning()}
      </p>
      <div className="mt-6 flex gap-2">
        <Button
          variant="primary"
          disabled={decide.isPending}
          onClick={() => decide.mutate(true)}
        >
          {m.web_hub_allow()}
        </Button>
        <Button
          disabled={decide.isPending}
          onClick={() => decide.mutate(false)}
        >
          {m.web_hub_deny()}
        </Button>
      </div>
      {decide.isError && <ErrorLine>{m.web_hub_failed()}</ErrorLine>}
    </>
  );
}
